import PropTypes from 'prop-types';
import { createContext, useEffect, useContext, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// hooks
import useLocalStorage from '../hooks/useLocalStorage';
// utils
import localStorageAvailable from '../utils/localStorageAvailable';
import muiTheme from '../utils/theme';
//
import { defaultSettings } from './config-setting';
import { PRESETS_LIST, CUSTOM_PRESET_KEY, normaliseHex } from './presets';
import { FONTS_LIST, DEFAULT_FONT, getFont } from './fonts';

// ----------------------------------------------------------------------

const initialState = {
  ...defaultSettings,
  // Mode
  onToggleMode: () => { },
  onChangeMode: () => { },
  // Direction
  onToggleDirection: () => { },
  onChangeDirection: () => { },
  onChangeDirectionByLang: () => { },
  // Layout
  onToggleLayout: () => { },
  onChangeLayout: () => { },
  // Contrast
  onToggleContrast: () => { },
  onChangeContrast: () => { },
  // Color
  onChangeColorPresets: () => { },
  presetsOption: PRESETS_LIST,
  // Font
  onChangeFontFamily: () => { },
  fontsOption: FONTS_LIST,
  // Stretch
  onToggleStretch: () => { },
  // Reset
  onResetSetting: () => { },
};

// ----------------------------------------------------------------------

export const SettingsContext = createContext(initialState);

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider');

  return context;
};

// ----------------------------------------------------------------------

SettingsProvider.propTypes = {
  children: PropTypes.node,
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useLocalStorage('settings', defaultSettings);

  const storageAvailable = localStorageAvailable();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  // -------------------------------------------------------------------
  // Migrations from previous storage layouts
  //
  // 1. Old `isDarkMode` boolean owned by DarkModeContext — copy it
  //    onto `settings.themeMode` exactly once if the user hasn't set
  //    the new field yet.
  //
  // 2. If neither legacy nor new mode is set, fall back to the OS
  //    preference instead of always defaulting to light.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!storageAvailable) return;

    const hasNewMode = settings && Object.prototype.hasOwnProperty.call(settings, 'themeMode')
      && (settings.themeMode === 'light' || settings.themeMode === 'dark');

    const legacy = localStorage.getItem('isDarkMode');
    if (!hasNewMode || legacy !== null) {
      let nextMode = settings?.themeMode || 'light';
      if (legacy !== null) {
        try {
          nextMode = JSON.parse(legacy) ? 'dark' : 'light';
        } catch {
          // ignore malformed value
        }
      } else if (!hasNewMode) {
        nextMode = prefersDarkMode ? 'dark' : 'light';
      }
      if (nextMode !== settings?.themeMode) {
        setSettings({ ...defaultSettings, ...settings, themeMode: nextMode });
      }
      // Drop the legacy key so this migration runs once.
      if (legacy !== null) localStorage.removeItem('isDarkMode');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------
  // Direction syncs to the current i18n language. Arabic = RTL.
  // Existing code path preserved.
  // -------------------------------------------------------------------
  const langStorage = storageAvailable ? localStorage.getItem('i18nextLng') : '';
  const isArabic = langStorage === 'en';

  // Mode
  const onToggleMode = useCallback(() => {
    const themeMode = settings.themeMode === 'light' ? 'dark' : 'light';
    setSettings({ ...settings, themeMode });
  }, [setSettings, settings]);

  const onChangeMode = useCallback(
    (event) => {
      const themeMode = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeMode });
    },
    [setSettings, settings]
  );

  // Direction
  const onToggleDirection = useCallback(() => {
    const themeDirection = settings.themeDirection === 'ltr' ? 'rtl' : 'ltr';
    setSettings({ ...settings, themeDirection });
  }, [setSettings, settings]);

  const onChangeDirection = useCallback(
    (event) => {
      const themeDirection = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeDirection });
    },
    [setSettings, settings]
  );

  const onChangeDirectionByLang = useCallback(
    (lang) => {
      const themeDirection = lang === 'en' ? 'ltr' : 'rtl';
      setSettings({ ...settings, themeDirection });
    },
    [setSettings, settings]
  );

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang('en');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic]);

  // Layout
  const onToggleLayout = useCallback(() => {
    const themeLayout = settings.themeLayout === 'vertical' ? 'mini' : 'vertical';
    setSettings({ ...settings, themeLayout });
  }, [setSettings, settings]);

  const onChangeLayout = useCallback(
    (event) => {
      const themeLayout = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeLayout });
    },
    [setSettings, settings]
  );

  // Contrast
  const onToggleContrast = useCallback(() => {
    const themeContrast = settings.themeContrast === 'default' ? 'bold' : 'default';
    setSettings({ ...settings, themeContrast });
  }, [setSettings, settings]);

  const onChangeContrast = useCallback(
    (event) => {
      const themeContrast = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeContrast });
    },
    [setSettings, settings]
  );

  // Color
  const onChangeColorPresets = useCallback(
    (event) => {
      const themeColorPresets = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeColorPresets });
    },
    [setSettings, settings]
  );

  // Custom color — accepts a hex string, normalises it, and switches the
  // active preset key to CUSTOM_PRESET_KEY in the same write so the
  // theme picks up the custom value on the next render. Passing null
  // or an invalid hex is a no-op.
  const onChangeCustomColor = useCallback(
    (hex) => {
      const normalised = normaliseHex(hex);
      if (!normalised) return;
      setSettings({
        ...settings,
        themeColorPresets: CUSTOM_PRESET_KEY,
        themeCustomColor: normalised,
      });
    },
    [setSettings, settings]
  );

  // Nav color — 'integrate' (sidebar painted with the primary color)
  // vs 'apparent' (sidebar uses neutral surface). Consumed by SideBar.
  const onChangeNavColor = useCallback(
    (event) => {
      const themeNavColor = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeNavColor });
    },
    [setSettings, settings]
  );

  // Font
  const onChangeFontFamily = useCallback(
    (event) => {
      const themeFontFamily = event?.target ? event.target.value : event;
      setSettings({ ...settings, themeFontFamily });
    },
    [setSettings, settings]
  );

  // Stretch
  const onToggleStretch = useCallback(() => {
    const themeStretch = !settings.themeStretch;
    setSettings({ ...settings, themeStretch });
  }, [setSettings, settings]);

  // Reset
  const onResetSetting = useCallback(() => {
    setSettings(defaultSettings);
  }, [setSettings]);

  // -------------------------------------------------------------------
  // Build the active MUI theme from current settings.
  //
  // Memoized on (themeMode, themeColorPresets, themeFontFamily,
  // themeDirection) only — other fields don't affect the MUI theme yet.
  // -------------------------------------------------------------------
  const activeTheme = useMemo(
    () =>
      muiTheme({
        mode: settings.themeMode,
        presetKey: settings.themeColorPresets,
        customColor: settings.themeCustomColor,
        fontKey: settings.themeFontFamily,
        direction: settings.themeDirection,
      }),
    [
      settings.themeMode,
      settings.themeColorPresets,
      settings.themeCustomColor,
      settings.themeFontFamily,
      settings.themeDirection,
    ]
  );

  // -------------------------------------------------------------------
  // Propagate the active font stack to a CSS custom property so global
  // CSS rules (in src/index.css) can pick up the font without going
  // through the MUI theme. We need this because the legacy `body, *
  // { font-family: ... !important }` rule overrides MUI's
  // typography.fontFamily on every element.
  // -------------------------------------------------------------------
  useEffect(() => {
    const font = getFont(settings.themeFontFamily);
    document.documentElement.style.setProperty('--app-font', font.stack);
  }, [settings.themeFontFamily]);

  const memoizedValue = useMemo(
    () => ({
      ...settings,
      // Mode
      onToggleMode,
      onChangeMode,
      // Direction
      onToggleDirection,
      onChangeDirection,
      onChangeDirectionByLang,
      // Layout
      onToggleLayout,
      onChangeLayout,
      // Contrast
      onChangeContrast,
      onToggleContrast,
      // Stretch
      onToggleStretch,
      // Color
      onChangeColorPresets,
      onChangeCustomColor,
      onChangeNavColor,
      presetsOption: PRESETS_LIST,
      // Font
      onChangeFontFamily,
      fontsOption: FONTS_LIST,
      defaultFontKey: DEFAULT_FONT,
      // Reset
      onResetSetting,
    }),
    [
      settings,
      onToggleMode,
      onChangeMode,
      onToggleDirection,
      onChangeDirection,
      onChangeDirectionByLang,
      onToggleLayout,
      onChangeLayout,
      onChangeContrast,
      onToggleContrast,
      onToggleStretch,
      onChangeColorPresets,
      onChangeCustomColor,
      onChangeNavColor,
      onChangeFontFamily,
      onResetSetting,
    ]
  );

  return (
    <SettingsContext.Provider value={memoizedValue}>
      <MuiThemeProvider theme={activeTheme}>
        {children}
      </MuiThemeProvider>
    </SettingsContext.Provider>
  );
}
